import React, { useState, useEffect } from 'react';
import { Globe } from 'lucide-react';

const SplashScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    // Phase 0: Initial (logo appears)
    // Phase 1: Logo animation complete, text appears
    // Phase 2: Loading bar starts
    // Phase 3: Fade out
    
    const timers = [
      setTimeout(() => setPhase(1), 500),   // Logo animation
      setTimeout(() => setPhase(2), 1200),  // Text appears
      setTimeout(() => setPhase(3), 2800),  // Start fade out
      setTimeout(() => onComplete(), 3500), // Complete
    ];

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-[#0a0a0c] transition-opacity duration-700 ${
        phase >= 3 ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]">
          <div 
            className={`absolute inset-0 bg-gradient-to-r from-[#00B8B8]/20 via-[#0EA5E9]/10 to-transparent rounded-full blur-3xl transition-all duration-1000 ${
              phase >= 1 ? 'scale-150 opacity-50' : 'scale-100 opacity-0'
            }`}
            style={{ animation: phase >= 1 ? 'pulse-glow 3s ease-in-out infinite' : 'none' }}
          />
        </div>
        
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-[#00B8B8]/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float-particle ${3 + Math.random() * 4}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo container */}
        <div 
          className={`relative transition-all duration-700 ease-out ${
            phase >= 1 ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
          }`}
        >
          {/* Glowing ring */}
          <div 
            className={`absolute inset-0 -m-4 rounded-full transition-all duration-1000 ${
              phase >= 1 ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              background: 'conic-gradient(from 0deg, #00B8B8, #0EA5E9, #00B8B8)',
              animation: phase >= 1 ? 'spin-slow 4s linear infinite' : 'none',
              filter: 'blur(8px)',
            }}
          />
          
          {/* Logo background */}
          <div 
            className={`relative w-28 h-28 bg-white rounded-2xl flex items-center justify-center shadow-[0_0_60px_rgba(0,184,184,0.5)] transition-all duration-500 overflow-hidden p-3 ${
              phase >= 1 ? 'rotate-0' : 'rotate-180'
            }`}
          >
            <img 
              src="/danwei-logo.png"
              alt="Danwei HR"
              className={`w-full h-full object-contain transition-all duration-500 ${
                phase >= 1 ? 'rotate-0 scale-100' : 'rotate-180 scale-0'
              }`}
            />
          </div>
        </div>

        {/* Title */}
        <div 
          className={`mt-8 text-center transition-all duration-700 delay-200 ${
            phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h1 className="text-5xl font-bold text-white tracking-tight">
            <span className="inline-block" style={{ animation: phase >= 1 ? 'text-reveal 0.6s ease-out forwards' : 'none' }}>
              Danwei
            </span>
            <span className="inline-block ml-3" style={{ animation: phase >= 1 ? 'text-reveal 0.6s ease-out 0.1s forwards' : 'none' }}>
              HR
            </span>
          </h1>
          <p 
            className={`mt-2 text-xl text-[#0EA5E9] transition-all duration-500 delay-500 ${
              phase >= 1 ? 'opacity-100' : 'opacity-0'
            }`}
          >
            Human Resource Suite
          </p>
        </div>

        {/* Tagline */}
        <p 
          className={`mt-6 text-[#6B7280] text-sm tracking-wider uppercase transition-all duration-500 ${
            phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          Enterprise HR Management
        </p>

        {/* Loading bar */}
        <div 
          className={`mt-8 w-64 h-1 bg-[#1a1a1f] rounded-full overflow-hidden transition-all duration-500 ${
            phase >= 2 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div 
            className="h-full bg-gradient-to-r from-[#00B8B8] to-[#0EA5E9] rounded-full"
            style={{
              width: phase >= 2 ? '100%' : '0%',
              transition: 'width 1.5s ease-out',
            }}
          />
        </div>

        {/* Loading text */}
        <p 
          className={`mt-4 text-[#4B5563] text-xs transition-all duration-300 ${
            phase >= 2 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {phase >= 3 ? 'Welcome!' : 'Loading your workspace...'}
        </p>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-[#00B8B8]/20 rounded-tl-3xl" />
      <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-[#00B8B8]/20 rounded-tr-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-[#00B8B8]/20 rounded-bl-3xl" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-[#00B8B8]/20 rounded-br-3xl" />

      {/* Version badge */}
      <div 
        className={`absolute bottom-8 text-[#374151] text-xs transition-all duration-500 ${
          phase >= 2 ? 'opacity-100' : 'opacity-0'
        }`}
      >
        v1.0.0 • Danwei HR
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        
        @keyframes float-particle {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.3; }
          25% { transform: translateY(-20px) translateX(10px); opacity: 0.6; }
          50% { transform: translateY(-10px) translateX(-10px); opacity: 0.4; }
          75% { transform: translateY(-30px) translateX(5px); opacity: 0.5; }
        }
        
        @keyframes text-reveal {
          from { 
            opacity: 0; 
            transform: translateY(20px) rotateX(-90deg); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) rotateX(0); 
          }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
